import { useState } from '#app'
import { computed, onMounted } from 'vue'
import { type RouteLocationNormalizedLoaded, useRoute } from 'vue-router'

import { dewebviewifyRouteName } from '~/scopes/webviews/utils'

import { INTRO_STORAGE_KEY } from '../const/config.const'
import { RAW_MATERIALS } from '../route-names'

// sessionStorage can throw in locked-down / private-mode browsers, so every
// access is guarded — a storage failure must never break the app.
function readSeen(): boolean {
  try {
    return sessionStorage.getItem(INTRO_STORAGE_KEY) === '1'
  } catch {
    return true // fail closed: skip the intro rather than crash
  }
}

function writeSeen(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(INTRO_STORAGE_KEY, '1')
    else sessionStorage.removeItem(INTRO_STORAGE_KEY)
  } catch {
    // In-memory `seen` still drives the current view; only a refresh forgets it.
  }
}

// The query keys that layer something over whatever route carries them (see
// `useModalState`): the `?metal=`/`?mine=` quick view, `?video=` cinema, `?view=` extended
// card. Named one by one rather than testing "is the query empty" — shared links arrive
// carrying `utm_*`, and the tour still belongs to those.
const LAYERED_PARAMS = ['metal', 'mine', 'video', 'view'] as const

// Did the visit *start* on the bare campaign home? Only there is the tour the opening move;
// every deeper URL was asked for by name, so it plays no onboarding at all.
//
// Deliberately not `BGMap`'s `isBareHome()`, which ignores `?view=`/`?video=`/`:id` because
// those don't move the camera — same words, different question. And deliberately not
// `useModalState`'s `activeModal === null` either: that reads `null` for an unknown impact
// type as well, so `/impact/bogus` would count as home and play the tour over it.
function isBareHomeEntry(route: RouteLocationNormalizedLoaded): boolean {
  const { name } = route
  // Webview routes are the same names suffixed, and their home is a home. An unmatched
  // route reads as "not bare", i.e. fails closed to no intro, as `readSeen()` does.
  if (typeof name !== 'string') return false
  if (dewebviewifyRouteName(name) !== RAW_MATERIALS.HOME) return false

  return LAYERED_PARAMS.every((key) => {
    const value = route.query[key]

    return !(Array.isArray(value) ? value[0] : value)
  })
}

// Reconcile the SSR default with the real per-tab flag once, after the first
// consumer hydrates on the client.
let synced = false

export function useIntroState() {
  // `true` on the server + first client render, so SSR emits the full UI for
  // crawlers. sessionStorage is client-only; the PageLoader curtain hides the
  // post-hydration flip.
  const seen = useState<boolean>(INTRO_STORAGE_KEY, () => true)
  // Read here, used in the sync below, so it is the route the visit *entered* on.
  const route = useRoute()

  onMounted(() => {
    if (synced) return
    synced = true
    // A deep entry leaves `seen` at its SSR `true`: assigning the same value is a no-op
    // for reactivity, so nothing re-renders and no consumer ever sees a frame of intro
    // (there is no PageLoader curtain to hide one — it is commented out in
    // `CampaignHome`). The flag itself is deliberately *not* written: nothing was seen,
    // and this sync being one-shot already keeps the tour away for the rest of the tab.
    // A reload at the bare home is a fresh entry, and shows it.
    //
    // The check must stay a snapshot taken here, never a computed: reactive, it would
    // resurrect the tour the moment the user navigated back to a bare home, and dismiss
    // it mid-step when `handleStart` navigates. It also must not be hoisted to module
    // scope — setup runs on the server, where module state is shared across requests,
    // which is the same reason `synced` is only ever touched from `onMounted`.
    if (readSeen() || !isBareHomeEntry(route)) return
    seen.value = false
  })

  const showIntro = computed(() => !seen.value)

  function dismissIntro() {
    seen.value = true
    writeSeen(true)
  }

  // Debug helper (Header restart): clear the flag so the intro re-appears. Skips the
  // entry check by design — the header navigates home first, and this sync never re-runs.
  function resetIntro() {
    seen.value = false
    writeSeen(false)
  }

  return { showIntro, dismissIntro, resetIntro }
}
